// Home / End caret movement for the text fields (composers, single-line inputs).
//
// Why this is not left to the browser: on macOS, Home/End do NOT move the caret
// inside a text field — Chrome jumps to the very start/end of the whole value and
// Safari just scrolls the page — so a buyer on a Mac keyboard (or any external
// full-size keyboard) presses Home mid-sentence and nothing useful happens. We
// implement the Windows/Linux behavior everywhere so the keys mean one thing:
//
//   Home / End              caret to the start / end of the CURRENT line
//   Ctrl/Cmd + Home / End   caret to the start / end of the whole value
//   + Shift                 extends the selection instead of collapsing it
//
// Lines here are logical (split on "\n"), not the visual rows a soft wrap paints,
// so inside a wrapped paragraph Home goes to the paragraph's start rather than the
// start of the visual row. Alt/Option combinations are left to the platform.

/** The slice of a React `KeyboardEvent` the decision reads. Structural, so unit
 *  tests can pass a plain object. */
export interface HomeEndKeyEvent {
  key: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  nativeEvent: { isComposing: boolean };
}

/** A text field's selection, in the shape `setSelectionRange` takes back. */
export interface CaretSelection {
  start: number;
  end: number;
  direction: "forward" | "backward" | "none";
}

function lineStart(value: string, index: number): number {
  // Guard index 0 explicitly: lastIndexOf clamps a negative fromIndex to 0, so a
  // value that STARTS with a newline would match its own first character and
  // report the caret's line as starting at 1.
  if (index <= 0) return 0;
  return value.lastIndexOf("\n", index - 1) + 1;
}

function lineEnd(value: string, index: number): number {
  const next = value.indexOf("\n", index);
  return next === -1 ? value.length : next;
}

/**
 * Pure decision: where should this Home/End keydown leave the selection?
 * Returns `null` when the key is not ours to handle (any other key, mid-IME
 * composition, or Alt/Option held) — the caller then leaves the event alone.
 */
export function homeEndSelection(
  event: HomeEndKeyEvent,
  value: string,
  selection: CaretSelection,
): CaretSelection | null {
  if (event.key !== "Home" && event.key !== "End") return null;
  if (event.nativeEvent.isComposing || event.altKey) return null;

  // The focus is the end the caret sits on; the anchor is the end Shift pivots
  // around. A collapsed selection has both in the same place either way. A
  // direction of "none" on a real range means the browser won't say which end
  // the caret is on (Firefox reports it for any selection set by script), so
  // there is no anchor to trust: take the focus as the end this key MOVES, which
  // grows the selection outward instead of silently discarding the far end.
  const backward =
    selection.direction === "none" && selection.start !== selection.end
      ? event.key === "Home"
      : selection.direction === "backward";
  const focus = backward ? selection.start : selection.end;
  const anchor = backward ? selection.end : selection.start;

  const wholeValue = event.ctrlKey || event.metaKey;
  const target =
    event.key === "Home"
      ? wholeValue
        ? 0
        : lineStart(value, focus)
      : wholeValue
        ? value.length
        : lineEnd(value, focus);

  if (!event.shiftKey) return { start: target, end: target, direction: "none" };
  if (target === anchor) return { start: target, end: target, direction: "none" };
  return target < anchor
    ? { start: target, end: anchor, direction: "backward" }
    : { start: anchor, end: target, direction: "forward" };
}

/** The DOM surface `applyHomeEndKey` touches — inputs and textareas both satisfy it. */
export interface HomeEndTarget {
  value: string;
  selectionStart: number | null;
  selectionEnd: number | null;
  selectionDirection?: string | null;
  setSelectionRange(start: number, end: number, direction?: "forward" | "backward" | "none"): void;
}

/**
 * Applies {@link homeEndSelection} to the event's field and consumes the keystroke.
 * Returns true when it handled the key.
 *
 * Safe to call unconditionally at the END of any field's keydown handler: it no-ops
 * on a key it doesn't own, on an event the handler already consumed
 * (`defaultPrevented`), and on a field that reports no selection (`type="email"` /
 * `"number"` expose `selectionStart` as null).
 */
export function applyHomeEndKey(
  event: HomeEndKeyEvent & {
    defaultPrevented?: boolean;
    preventDefault(): void;
    currentTarget: HomeEndTarget;
  },
): boolean {
  if (event.defaultPrevented) return false;
  const field = event.currentTarget;
  if (field.selectionStart === null || field.selectionEnd === null) return false;

  const next = homeEndSelection(event, field.value, {
    start: field.selectionStart,
    end: field.selectionEnd,
    // Pass the browser's answer through as-is, "none" included: coercing it to
    // "forward" would claim an anchor the browser never gave us.
    direction:
      field.selectionDirection === "backward"
        ? "backward"
        : field.selectionDirection === "forward"
          ? "forward"
          : "none",
  });
  if (!next) return false;

  event.preventDefault();
  field.setSelectionRange(next.start, next.end, next.direction);
  return true;
}
